import { Type, Transform } from 'class-transformer'
import { some } from 'lodash'
import { DateTime } from 'luxon'
import { InvoiceStatus } from 'types/types'

interface LastFinalizationError {
    code: string
    doc_url: string
    message: string
    param: string
    payment_method_type: string
    type: string
}

export default class Invoice {
    id!: number
    billing_account_id!: number
    stripe_id!: string
    customer!: string
    description!: string
    attempt_count!: number
    payment_intent_id!: string
    hosted_invoice_url!: string
    invoice_pdf!: string
    invoice_number!: string
    receipt_number!: string
    status!: InvoiceStatus
    last_finalization_error!: LastFinalizationError

    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    created_at!: DateTime | null

    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    finalized_at!: DateTime | null

    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    marked_uncollectible_at!: DateTime | null

    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    paid_at!: DateTime | null

    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    voided_at!: DateTime | null

    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    deleted_at!: DateTime | null

    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    due_at!: DateTime | null

    attempted!: Boolean

    charge_id!: string
    charge_error!: string
    charge_payment_method_id!: string

    amount_due!: number
    amount_paid!: number
    amount_remaining!: number

    practice_name!: string
    parent_company!: string

    // annotate billing account
    billing_name!: string
    billing_email!: string
    customer_id!: string
    practice_group_id!: number
    practice_group_name!: string
    invoice_cycle!: string
    invoice_autopay!: string
    account_number!: string
    sort_code!: string

    // annotate late fees
    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    late_cycle_start_date!: DateTime | null
    
    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    late_cycle_end_date!: DateTime | null
    
    late_days!: number
    late_cycles!: number
    
    @Type(() => DateTime)
    @Transform(({ value }) => value ? DateTime.fromISO(value) : null)
    next_late_cycle_date!: DateTime | null
    
    num_matches!: number
    late_amount_per_cycle!: number
    late_amount_cumulative!: number
    late_amount_invoiced!: number
    late_amount_not_yet_invoiced!: number

    // annotate invoice type
    is_late_fees!: boolean
    is_job_payments!: boolean

    created_at_label(tz: string = 'Europe/London', format: string = 'ccc, LLL dd, yyyy') {
        const days_ago = Math.abs(Math.round(this.created_at?.diffNow('days').days ?? 0))
        const created_at = this.created_at?.setZone(tz).toFormat(format) ?? 'N/A'
        return `${created_at} (${days_ago} days ago)`
    }

    finalized_at_label(tz: string = 'Europe/London', format: string = 'ccc, LLL dd, yyyy') {
        const finalized_at = this.finalized_at?.setZone(tz).toFormat(format) ?? 'N/A'
        return finalized_at
    }

    late_cycle_start_date_label(tz: string = 'Europe/London', format: string = 'ccc, LLL dd, yyyy') {
        const late_cycle_start_date = this.late_cycle_start_date?.setZone(tz).toFormat(format) ?? 'N/A'
        return late_cycle_start_date
    }

    next_late_cycle_date_label(tz: string = 'Europe/London', format: string = 'ccc, LLL dd, yyyy') {
        const next_late_cycle_date = this.next_late_cycle_date?.setZone(tz).toFormat(format) ?? 'N/A'
        return next_late_cycle_date
    }

    get amount_label() {
        return `£${(this.amount_due / 100).toFixed(2)}`
    }

    get status_label() {
        if (this.status === 'paid') {
            return 'Paid'
        } else if (this.charge_error) {
            return `Payment Failed (${this.charge_error})`
        } else if (this.status === 'open' && this.attempted) {
            return `${this.status} (Payment Initiated)`
        }
        return this.status
    }

    get is_overdue() {
        return this.status === 'open' && (this.created_at?.diffNow('days').days ?? 0) < -1
    }

    get practice_group_label() {
        if (this.practice_group_name) {
            return this.practice_group_name
        } else if (this.parent_company) {
            return `Independent (${this.parent_company})`
        } else {
            return 'Independent'
        }
    }
}